/**
 * Tenant Branding Provider
 *
 * Applies tenant-specific branding to the application:
 * - Custom primary/secondary colors via CSS custom properties
 * - Custom logo display
 * - Custom app name in document title
 * - Custom favicon
 *
 * Part of: AI Platform Phase 3.1 - UI/UX Enhancement & Tenant Customization
 */

import React, { useEffect, ReactNode } from 'react';
import { useTenantOptional, TenantBranding } from '../contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

interface TenantBrandingProviderProps {
  children: ReactNode;
  /** Default app name when tenant has no custom name */
  defaultAppName?: string;
}

// =============================================================================
// Color Utility Functions
// =============================================================================

/**
 * Convert hex color to RGB components
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Generate lighter/darker variations of a color
 */
function adjustBrightness(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value: number) => {
    const adjusted = Math.round(value + (255 - value) * (percent / 100));
    return Math.min(255, Math.max(0, adjusted));
  };

  if (percent > 0) {
    // Lighten
    return `#${adjust(rgb.r).toString(16).padStart(2, '0')}${adjust(rgb.g).toString(16).padStart(2, '0')}${adjust(rgb.b).toString(16).padStart(2, '0')}`;
  } else {
    // Darken
    const darken = (value: number) => Math.round(value * (1 + percent / 100));
    return `#${darken(rgb.r).toString(16).padStart(2, '0')}${darken(rgb.g).toString(16).padStart(2, '0')}${darken(rgb.b).toString(16).padStart(2, '0')}`;
  }
}

// =============================================================================
// CSS Custom Property Application
// =============================================================================

/**
 * Apply branding colors as CSS custom properties
 */
function applyBrandingColors(branding: TenantBranding): void {
  const root = document.documentElement;

  // Primary color - used for main accent elements (buttons, links, highlights)
  if (branding.primaryColor) {
    const primary = branding.primaryColor;
    const primaryRgb = hexToRgb(primary);

    root.style.setProperty('--primary', primary);
    root.style.setProperty('--accent-secondary', primary); // Map to accent-secondary in design system
    root.style.setProperty('--primary-hover', adjustBrightness(primary, 15));
    root.style.setProperty('--primary-active', adjustBrightness(primary, -10));

    if (primaryRgb) {
      root.style.setProperty('--primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }
  }

  // Secondary color - used for secondary accents (info badges, secondary buttons)
  if (branding.secondaryColor) {
    const secondary = branding.secondaryColor;
    const secondaryRgb = hexToRgb(secondary);

    root.style.setProperty('--accent-primary', secondary); // Map to accent-primary in design system
    root.style.setProperty('--secondary', secondary);
    root.style.setProperty('--secondary-hover', adjustBrightness(secondary, 15));

    if (secondaryRgb) {
      root.style.setProperty('--accent-primary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    }
  }
}

/**
 * Remove applied branding colors (reset to defaults)
 */
function removeBrandingColors(): void {
  const root = document.documentElement;
  const properties = [
    '--primary',
    '--primary-hover',
    '--primary-active',
    '--primary-rgb',
    '--accent-secondary',
    '--accent-primary',
    '--accent-primary-rgb',
    '--secondary',
    '--secondary-hover',
  ];

  properties.forEach((prop) => {
    root.style.removeProperty(prop);
  });
}

/**
 * Update favicon dynamically
 */
function updateFavicon(faviconUrl: string | null): void {
  if (!faviconUrl) return;

  // Find existing favicon link or create new one
  let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

/**
 * Reset favicon to default
 */
function resetFavicon(): void {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  if (link) {
    link.href = '/favicon.ico';
  }
}

// =============================================================================
// Component
// =============================================================================

export const TenantBrandingProvider: React.FC<TenantBrandingProviderProps> = ({
  children,
  defaultAppName = 'AI Platform',
}) => {
  const tenantContext = useTenantOptional();
  const branding = tenantContext?.tenant?.branding;

  // Apply branding colors
  useEffect(() => {
    if (branding) {
      applyBrandingColors(branding);
    }

    return () => {
      removeBrandingColors();
    };
  }, [branding]);

  // Update document title with app name
  useEffect(() => {
    const appName = branding?.appName || defaultAppName;
    document.title = appName;

    return () => {
      document.title = defaultAppName;
    };
  }, [branding?.appName, defaultAppName]);

  // Update favicon
  useEffect(() => {
    if (branding?.favicon) {
      updateFavicon(branding.favicon);
    }

    return () => {
      resetFavicon();
    };
  }, [branding?.favicon]);

  return <>{children}</>;
};

// =============================================================================
// Hook for accessing branding in components
// =============================================================================

export interface TenantBrandingInfo {
  primaryColor: string;
  secondaryColor: string;
  logo: string | null;
  appName: string;
  hasBranding: boolean;
}

/**
 * Hook to access tenant branding information
 * Returns default values when no tenant or branding is available
 */
export function useTenantBranding(): TenantBrandingInfo {
  const tenantContext = useTenantOptional();
  const branding = tenantContext?.tenant?.branding;

  return {
    primaryColor: branding?.primaryColor || '#7c3aed',
    secondaryColor: branding?.secondaryColor || '#06b6d4',
    logo: branding?.logo || null,
    appName: branding?.appName || 'AI Platform',
    hasBranding: !!branding && !!(branding.primaryColor || branding.secondaryColor || branding.logo || branding.appName),
  };
}

export default TenantBrandingProvider;
